import { db } from '@/firebase/init'
import { LabAccount } from '@team-milva/lab-models/labAccount'
import { CustomerAccount } from '@team-milva/lab-models/customerAccount'
import { InsuranceCustomerAccount } from '@team-milva/lab-models/insuranceCustomerAccount'
import { LabCustomerUserBusiness } from '@/globals/typescript/models/LabCustomerUserBusiness'
import { LabCustomerUserInsurance } from '@/globals/typescript/models/LabCustomerUserInsurance'
import { updateThemeColors } from '@/globals/typescript/utils/themeUtils'
import { useUserStore } from '@/stores/userStore'
import {
  customerOwnerApi,
  customerOwnerContext,
} from '@/globals/typescript/utils/api/customerOwnerApi'
import {
  LAB_CUSTOMER_LANGUAGES,
  type ILabLanguage,
  type TLabCustomerLanguageCode,
} from '@team-milva/lab-models/language'
import {
  LAB_MANAGEMENT_CURRENCIES,
  type ILabCurrency,
  type TLabManagementCurrencyCode,
} from '@team-milva/lab-models/currency'
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore'
import { sortBy } from 'lodash-es'
import { defineStore } from 'pinia'

/**
 * The account the signed-in user belongs to (MIL-402): a business customer (`CustomerAccount`) or
 * an insurance customer (`InsuranceCustomerAccount`). Both carry everything this app reads off the
 * account — `name`, `companyDetails`, `options.isProjectsEnabled` / `isPricesHiddenInCustomerApp`,
 * `supportedLocales` / `defaultLocale` — so callers use it without branching. Only a business
 * customer has a primary contact user; read it through the `primaryContact` getter.
 */
export type TCustomerOwnerAccount = CustomerAccount | InsuranceCustomerAccount

/** A colleague on the same account — a business or an insurance user, matching the account. */
export type TTeamMember = LabCustomerUserBusiness | LabCustomerUserInsurance

export const useAccountStore = defineStore('account', {
  state: () => ({
    labAccount: null as LabAccount | null,
    customerAccount: null as TCustomerOwnerAccount | null,
    allLabAccountUsers: [] as TTeamMember[],

    unsubscribeLabAccount: null as (() => void) | null,
    unsubscribeCustomerAccount: null as (() => void) | null,
    unsubscribeLabAccountUsers: null as (() => void) | null,
  }),

  actions: {
    getLabAccountFromUser(): Promise<LabAccount | null> {
      if (this.labAccountLoaded) {
        return Promise.resolve(this.labAccount)
      }

      const userStore = useUserStore()

      return new Promise((resolve) => {
        if (!userStore.currentUser) {
          return resolve(null)
        }

        let resolved = false
        const unsubscribe = onSnapshot(
          doc(
            db,
            'Apps',
            '-lab-management-',
            'LabAccounts',
            userStore.currentUser.labAccountID
          ),
          (snap) => {
            const labAccount = new LabAccount(snap.data() as LabAccount)

            if (labAccount) {
              this.labAccount = labAccount
              this.unsubscribeLabAccount = unsubscribe

              updateThemeColors({
                themeColors: labAccount.themes?.labManagementColors || {},
              })

              if (!resolved) {
                resolved = true
                resolve(this.labAccount)
              }
            }
          }
        )
      })
    },

    // Loads the signed-in user's own account from the collection its `customer.type` names
    // (MIL-402): `CustomerAccounts` for a business user, `InsuranceCustomerAccounts` for an
    // insurance user. A private user has no account.
    getCustomerAccount(): Promise<TCustomerOwnerAccount | null> {
      if (this.customerAccountLoaded) {
        return Promise.resolve(this.customerAccount)
      }

      const owner = customerOwnerContext(useUserStore().currentUser)

      return new Promise((resolve) => {
        if (!owner) {
          return resolve(null)
        }

        const isInsurance = owner.customer.type === 'insurance'
        let resolved = false
        const unsubscribe = onSnapshot(
          doc(
            db,
            'Apps',
            '-lab-management-',
            isInsurance ? 'InsuranceCustomerAccounts' : 'CustomerAccounts',
            owner.customer.id
          ),
          (snap) => {
            if (snap.exists()) {
              this.customerAccount = isInsurance
                ? new InsuranceCustomerAccount(snap.data() as InsuranceCustomerAccount)
                : new CustomerAccount(snap.data() as CustomerAccount)
              this.unsubscribeCustomerAccount = unsubscribe

              if (!resolved) {
                resolved = true
                resolve(this.customerAccount)
              }
            }
          }
        )
      })
    },

    // Only called when current user has the 'admin' role (MIL-137)
    getAllLabAccountUsers(): Promise<TTeamMember[]> {
      if (this.allLabAccountUsersLoaded) {
        return Promise.resolve(this.allLabAccountUsers)
      }

      const owner = customerOwnerContext(useUserStore().currentUser)

      return new Promise((resolve) => {
        let resolved = false
        if (!this.customerAccount || !owner) {
          return resolve([])
        }

        const q = query(
          collection(db, 'Apps', '-lab-customer-', 'Users'),
          where('customer.type', '==', owner.customer.type),
          where('customer.id', '==', this.customerAccount.id)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          const users: TTeamMember[] = []
          querySnapshot.forEach((doc) => {
            const data = doc.data()
            users.push(
              data.type === 'lab-customer-user-insurance'
                ? new LabCustomerUserInsurance(data as LabCustomerUserInsurance)
                : new LabCustomerUserBusiness(data as LabCustomerUserBusiness)
            )
          })
          this.allLabAccountUsers = users
          this.unsubscribeLabAccountUsers = unsubscribe

          if (!resolved) {
            resolved = true
            resolve(users)
          }
        })
      })
    },

    // Owner-only action, gated in the UI. The server lets an account admin change a teammate's
    // record (and only on their own account); a member is refused there too.
    async updateTeamMemberActiveState(
      teamMember: TTeamMember,
      activeState: 'active' | 'inactive'
    ): Promise<void> {
      const owner = customerOwnerContext(teamMember)
      if (!owner) throw new Error('Team member has no customer account')

      const result = await customerOwnerApi(owner, 'patch', '/users/{userID}', {
        path: { userID: teamMember.id },
        body: { activeState },
      })

      if (!result.success) throw new Error(result.code)
    },

    resetStore() {
      this.labAccount = null
      this.customerAccount = null
      this.allLabAccountUsers = []

      if (this.unsubscribeLabAccount) {
        this.unsubscribeLabAccount()
        this.unsubscribeLabAccount = null
      }
      if (this.unsubscribeCustomerAccount) {
        this.unsubscribeCustomerAccount()
        this.unsubscribeCustomerAccount = null
      }
      if (this.unsubscribeLabAccountUsers) {
        this.unsubscribeLabAccountUsers()
        this.unsubscribeLabAccountUsers = null
      }
    },
  },

  getters: {
    labAccountLoaded: (state) => !!state.unsubscribeLabAccount,
    customerAccountLoaded: (state) => !!state.unsubscribeCustomerAccount,
    allLabAccountUsersLoaded: (state) => !!state.unsubscribeLabAccountUsers,

    // Reads customerLanguages — the dedicated customer-facing field — never labLanguages,
    // which is lab-management staff-only (EN/DK only). Fixed 2026-09-16: this getter
    // previously read labLanguages, a known bug diagnosed but deferred by the
    // "Language & Locale Configuration Overhaul" plan. See "Per-Lab & Per-Customer
    // Language Selection", Phase 1.
    //
    // Once a CustomerAccount is also loaded (Phase 2), its own supportedLocales narrows
    // this further — the account's own configured set, not just the lab-wide ceiling.
    // supportedLocales stores lowercase locale strings (TLabCustomerLocale), unlike
    // customerLanguages' uppercase codes, so the narrowing filters on `.locale` rather
    // than re-keying LAB_CUSTOMER_LANGUAGES. An unset or empty supportedLocales falls
    // back to the lab-wide list unchanged — the account hasn't configured its own set.
    availableLanguages: (state): ILabLanguage[] => {
      const account = state.labAccount
      if (!account) {
        return sortBy(Object.values(LAB_CUSTOMER_LANGUAGES), 'code')
      }

      const labLanguages = sortBy(
        account.customerLanguages
          .map((code) => LAB_CUSTOMER_LANGUAGES[code as TLabCustomerLanguageCode])
          .filter((l) => l !== undefined) as ILabLanguage[],
        'code'
      )

      const supportedLocales = state.customerAccount?.supportedLocales
      if (!supportedLocales || supportedLocales.length === 0) {
        return labLanguages
      }

      return sortBy(
        labLanguages.filter((language) =>
          (supportedLocales as readonly string[]).includes(language.locale)
        ),
        'code'
      )
    },

    availableCurrencies: (state): ILabCurrency[] => {
      const account = state.labAccount
      if (!account) {
        return sortBy(Object.values(LAB_MANAGEMENT_CURRENCIES), 'code')
      }
      return sortBy(
        account.labCurrencies
          .map((code) => LAB_MANAGEMENT_CURRENCIES[code as TLabManagementCurrencyCode])
          .filter((c) => c !== undefined) as ILabCurrency[],
        'code'
      )
    },

    defaultCurrencyCode: (state): TLabManagementCurrencyCode => {
      return state.labAccount?.defaultCurrencyCode ?? 'DKK'
    },

    // `CustomerAccount.primaryContactUserID` is only an ID reference, not a
    // denormalized object — resolve it against the already-loaded team member
    // list (see AccountTeamView.vue for the same resolve-by-ID pattern).
    // Returns undefined while `allLabAccountUsers` hasn't loaded yet, or if the
    // referenced user is no longer part of the account.
    // A business and an insurance customer both have one (MIL-402).
    primaryContact: (state): TTeamMember | undefined => {
      const primaryContactUserID = state.customerAccount?.primaryContactUserID ?? null
      if (!primaryContactUserID) return undefined
      return state.allLabAccountUsers.find((x) => x.id === primaryContactUserID)
    },

    // "Lab Customer — Projects, Floor Plans & Samples" plan — reads the real
    // per-CustomerAccount toggle (lab-management's "Projekter" switch on the
    // customer account, default false). Every call site in this app reads
    // Projects visibility through this getter.
    isProjectsEnabled: (state): boolean => {
      return !!state.customerAccount?.options.isProjectsEnabled
    },

    // Reads the per-CustomerAccount "Skjul priser i kunde-app" toggle in
    // lab-management (default false). Every price surface in this app reads
    // through this getter, so hiding prices is a one-place decision.
    isPricesHiddenInCustomerApp: (state): boolean => {
      return !!state.customerAccount?.options.isPricesHiddenInCustomerApp
    },
  },
})
